/**
 * ACADÉMIE — section membre (dans /membres).
 * Vue par discipline : techniques (curriculum), programmes 20/30/45 min, carnet/progression.
 * Accès conditionnel : abonné en ligne OU membre actif. Sinon → débloquer (abonnement Stripe).
 */
import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { memberApi } from "../lib/api";
import { toast } from "sonner";
import { OnlineCarnet } from "./academy/OnlineCarnet";
import {
  GraduationCap, Lock, ArrowRight, CircleNotch, PlayCircle, Barbell,
  Medal, CaretRight, Timer, ShieldCheck, Path, Ranking, Trophy, X, CheckCircle,
} from "@phosphor-icons/react";

const DURATION_LABEL = { 20: "20 min", 30: "30 min", 45: "45 min" };

export default function AcademyMemberSection({ email }) {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCode, setOpenCode] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    memberApi.get("/member/academy/overview")
      .then((r) => setOverview(r.data))
      .catch(() => setOverview(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Retour de paiement Stripe → activation par polling
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("academy") === "success" && params.get("session_id")) {
      const sid = params.get("session_id");
      toast.loading("Activation de ton accès Académie…", { id: "acad" });
      const poll = async (tries = 0) => {
        try {
          const r = await memberApi.get(`/academy/status/${sid}`);
          if (r.data.payment_status === "paid") {
            toast.success("Accès Académie activé ! 🥋", { id: "acad" });
            window.history.replaceState({}, "", "/membres");
            load();
            return;
          }
        } catch (e) { /* retry */ }
        if (tries < 20) setTimeout(() => poll(tries + 1), 3000);
        else toast.error("Activation en attente — actualise dans 1 min.", { id: "acad" });
      };
      poll();
    }
  }, [load]);

  if (loading) {
    return (
      <section className="border border-white/10 bg-white/[0.02] p-6 mb-8" data-testid="academy-section-loading">
        <CircleNotch size={28} className="text-[#C9A961] animate-spin" />
      </section>
    );
  }
  if (!overview) return null;

  return (
    <section className="border border-[#C9A961]/30 bg-gradient-to-br from-[#C9A961]/[0.06] to-transparent p-6 mb-8 kz-fade-up" data-testid="academy-section">
      <div className="flex items-center gap-3 mb-1">
        <GraduationCap size={22} weight="fill" className="text-[#C9A961]" />
        <span className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#C9A961]">Formations en ligne</span>
      </div>
      <h2 className="kz-display text-2xl text-white mb-1">Académie</h2>
      <p className="kz-body text-sm text-white/60 mb-6">
        Technique, progression et entraînement réunis dans une seule expérience.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {overview.disciplines.map((d) => (
          <DisciplineCard key={d.code} d={d} email={email}
            onOpen={() => setOpenCode(d.code)}
            products={overview.products} onUnlocked={load} />
        ))}
      </div>

      {openCode && (
        <DisciplineDetail code={openCode} onClose={() => setOpenCode(null)} />
      )}
    </section>
  );
}

function DisciplineCard({ d, email, onOpen, products, onUnlocked }) {
  const [busy, setBusy] = useState(false);
  const product = products.find((p) => p.code === d.code);

  async function unlock(productCode) {
    setBusy(true);
    try {
      const r = await memberApi.post("/academy/checkout", {
        email, full_name: email.split("@")[0],
        product_code: productCode, origin_url: window.location.origin,
      });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer le paiement.");
      setBusy(false);
    }
  }

  return (
    <div className="border p-5 flex flex-col transition-colors"
      style={{ borderColor: d.unlocked ? `${d.accent}66` : "rgba(255,255,255,0.08)" }}
      data-testid={`academy-discipline-${d.code}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="w-2.5 h-2.5 rounded-full" style={{ background: d.accent }} />
        {d.unlocked
          ? <ShieldCheck size={18} weight="fill" className="text-green-400" data-testid={`academy-unlocked-${d.code}`} />
          : <Lock size={16} className="text-white/40" data-testid={`academy-locked-${d.code}`} />}
      </div>
      <h3 className="kz-display text-lg text-white leading-tight">{d.name}</h3>
      <p className="kz-body text-xs text-white/55 mt-1 flex-1">{d.tagline}</p>
      <div className="kz-mono text-[10px] tracking-wider text-white/40 mt-3">
        {d.technique_count} techniques · {d.program_count} programmes
      </div>
      {d.unlocked ? (
        <button onClick={onOpen} data-testid={`academy-enter-${d.code}`}
          className="mt-4 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/40 bg-white/[0.03] hover:bg-white/[0.06] text-white text-sm py-2.5 transition-colors">
          Entrer <ArrowRight size={15} />
        </button>
      ) : (
        <button onClick={() => unlock(d.code)} disabled={busy} data-testid={`academy-unlock-${d.code}`}
          className="mt-4 inline-flex items-center justify-center gap-2 text-black text-sm font-semibold py-2.5 transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: "#C9A961" }}>
          {busy ? <CircleNotch size={15} className="animate-spin" />
            : <>Débloquer · {product ? `${product.price_cad.toFixed(2)} $/mois` : ""}</>}
        </button>
      )}
    </div>
  );
}

function TechRow({ t, onPlay, watched }) {
  const clickable = t.has_video && t.embed_url;
  const isWatched = watched || t.watched;
  return (
    <div onClick={clickable ? () => onPlay(t.embed_url, t.name, t.id) : undefined}
      className={`px-4 py-2.5 flex items-center gap-3 text-sm text-white/80 ${clickable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
      data-testid={clickable ? `academy-play-${t.id}` : undefined}>
      {isWatched
        ? <CheckCircle size={14} weight="fill" className="text-green-400 shrink-0" data-testid={`academy-watched-${t.id}`} />
        : <CaretRight size={12} className="text-white/30 shrink-0" />}
      <span className="flex-1">{t.name}</span>
      {t.category && <span className="kz-mono text-[10px] text-white/35 uppercase">{t.category}</span>}
      {clickable
        ? <PlayCircle size={18} weight="fill" className="text-[#C9A961]" />
        : <span className="kz-mono text-[9px] text-white/25 border border-white/10 px-1.5 py-0.5">VIDÉO À VENIR</span>}
    </div>
  );
}

function VideoPlayer({ videoId, title, onClose }) {
  return (
    <div className="fixed inset-0 z-[140] bg-black/90 flex items-center justify-center p-4" data-testid="academy-video-player" onClick={onClose}>
      <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-white text-sm">{title}</span>
          <button onClick={onClose} data-testid="academy-video-close" className="text-white/60 hover:text-white"><X size={22} /></button>
        </div>
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe className="absolute inset-0 w-full h-full border border-white/15" title={title}
            src={videoId}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen />
        </div>
      </div>
    </div>
  );
}

function DisciplineDetail({ code, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("techniques");
  const [player, setPlayer] = useState(null); // {id, title}
  const [justWatched, setJustWatched] = useState(new Set());

  const onPlay = (id, title, techId) => {
    setPlayer({ id, title });
    if (techId && !justWatched.has(techId)) {
      memberApi.post("/member/academy/watched", { discipline: code, technique_id: techId })
        .then(() => setJustWatched((p) => new Set(p).add(techId)))
        .catch(() => {});
    }
  };

  useEffect(() => {
    setData(null);
    setJustWatched(new Set());
    memberApi.get(`/member/academy/discipline/${code}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [code]);

  const progress = data?.progress;
  const nextBelt = progress?.next_belt;
  const pct = Math.max(0, Math.min(100, progress?.global_average || 0));

  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="academy-detail-modal">
      <div className="w-full max-w-3xl bg-[#0C0A09] border border-white/15 my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0C0A09] z-10">
          <h3 className="kz-display text-xl text-white">
            {data?.discipline?.name || "Chargement…"}
          </h3>
          <button onClick={onClose} data-testid="academy-detail-close"
            className="text-white/50 hover:text-white text-sm border border-white/15 px-3 py-1.5">Fermer</button>
        </div>

        {!data && <div className="p-8 text-center"><CircleNotch size={28} className="text-[#C9A961] animate-spin inline" /></div>}

        {data && !data.error && (
          <div className="p-5">
            {progress && (
              <div className="border border-white/10 bg-white/[0.02] p-4 mb-5" data-testid="academy-progress">
                <div className="flex items-center gap-4">
                  <Medal size={26} weight="fill" className="text-[#C9A961]" />
                  <div className="flex-1">
                    <div className="kz-mono text-[10px] tracking-wider text-white/50 uppercase">Ma progression</div>
                    <div className="text-white text-sm">{progress.current_belt} → {progress.target_belt}</div>
                  </div>
                  <div className="text-right">
                    <div className="kz-display text-2xl text-white">{progress.global_average}%</div>
                    <div className="kz-mono text-[10px] text-white/40">{progress.technique_count} techniques</div>
                  </div>
                </div>
                {nextBelt && (
                  <div className="mt-3" data-testid="academy-nextbelt">
                    <div className="flex justify-between kz-mono text-[10px] text-white/50 mb-1">
                      <span>Progression globale</span>
                      <span>Prochaine ceinture : {nextBelt}</span>
                    </div>
                    <div className="h-2 bg-white/10 overflow-hidden">
                      <div className="h-full bg-[#C9A961] transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
              </div>
            )}

            {data.total_videos > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-4 mb-5" data-testid="academy-completion">
                <div className="flex justify-between kz-mono text-[10px] text-white/50 mb-1">
                  <span>Contenu vidéo complété</span>
                  <span>{data.watched_videos}/{data.total_videos} vidéos · {data.completion_percent}%</span>
                </div>
                <div className="h-2 bg-white/10 overflow-hidden">
                  <div className="h-full bg-green-500 transition-all" style={{ width: `${data.completion_percent}%` }} />
                </div>
              </div>
            )}

            {data.recommended_program && (
              <div className="border border-[#C9A961]/40 bg-[#C9A961]/[0.06] p-4 mb-5 flex items-center gap-3" data-testid="academy-recommendation">
                <Barbell size={22} weight="fill" className="text-[#C9A961] shrink-0" />
                <div className="flex-1">
                  <div className="kz-mono text-[10px] tracking-wider text-[#C9A961] uppercase">Recommandé pour toi</div>
                  <div className="text-white text-sm">{data.recommended_program.title} · {DURATION_LABEL[data.recommended_program.duration_min]}</div>
                </div>
                <button onClick={() => setTab("programmes")} data-testid="academy-reco-cta"
                  className="text-black bg-[#C9A961] text-xs font-semibold px-3 py-2 hover:opacity-90 whitespace-nowrap">
                  Commencer
                </button>
              </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                ["carnet", "🥋 Mon carnet (répétitions)"],
                ["techniques", "Techniques"],
                ["ceintures", "Ceintures"],
                ["combinaisons", data.combo_label || "Combinaisons"],
                ["programmes", "Entraînements"],
                ["competition", "Compétition"],
              ].map(([t, label]) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`academy-tab-${t}`}
                  className={`px-4 py-2 text-sm border transition-colors ${tab === t ? "border-[#C9A961] text-[#C9A961]" : "border-white/10 text-white/50 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            {tab === "carnet" && (
              <OnlineCarnet code={code} discName={data.discipline?.name} beltGroups={data.belt_groups || []} currentBelt={progress?.current_belt} />
            )}

            {tab === "techniques" && (
              <div className="space-y-4" data-testid="academy-techniques">
                {(data.belt_groups || []).length === 0 && (
                  <p className="text-white/50 text-sm">Aucune technique publiée pour l'instant.</p>
                )}
                {(data.belt_groups || []).map((g) => (
                  <div key={g.belt} className="border border-white/10">
                    <div className="px-4 py-2.5 bg-white/[0.03] kz-mono text-xs tracking-wider text-white/70 flex items-center gap-2">
                      <Barbell size={14} className="text-[#C9A961]" /> Ceinture {g.belt} · {g.techniques.length}
                    </div>
                    <div className="divide-y divide-white/5">
                      {g.techniques.map((t) => <TechRow key={t.id} t={t} onPlay={onPlay} watched={justWatched.has(t.id)} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "ceintures" && (
              <div className="space-y-2" data-testid="academy-ceintures">
                {(data.belts || []).length === 0 && (
                  <p className="text-white/50 text-sm">Parcours de ceintures à venir.</p>
                )}
                {(data.belts || []).map((b) => (
                  <div key={b.belt}
                    className={`flex items-center gap-3 border px-4 py-3 ${b.is_current ? "border-[#C9A961] bg-[#C9A961]/[0.06]" : "border-white/10 bg-white/[0.02]"}`}
                    data-testid={`academy-belt-${b.belt_order}`}>
                    <Ranking size={18} weight={b.is_current ? "fill" : "regular"}
                      className={b.is_current ? "text-[#C9A961]" : "text-white/40"} />
                    <span className="flex-1 text-white/85 text-sm">Ceinture {b.belt}</span>
                    {b.is_current && <span className="kz-mono text-[9px] text-[#C9A961] border border-[#C9A961]/50 px-1.5 py-0.5">ACTUELLE</span>}
                    <span className="kz-mono text-[10px] text-white/40">{b.technique_count} tech.</span>
                  </div>
                ))}
              </div>
            )}

            {tab === "combinaisons" && (
              <div className="space-y-4" data-testid="academy-combinaisons">
                {(data.combinations || []).length === 0 && (
                  <div className="border border-dashed border-white/15 p-6 text-center">
                    <Path size={26} className="text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">{data.combo_label || "Combinaisons"} en préparation.</p>
                  </div>
                )}
                {(data.combinations || []).map((g) => (
                  <div key={g.belt} className="border border-white/10">
                    <div className="px-4 py-2.5 bg-white/[0.03] kz-mono text-xs tracking-wider text-white/70 flex items-center gap-2">
                      <Path size={14} className="text-[#C9A961]" /> Ceinture {g.belt} · {g.items.length}
                    </div>
                    <div className="divide-y divide-white/5">
                      {g.items.map((t) => <TechRow key={t.id} t={t} onPlay={onPlay} watched={justWatched.has(t.id)} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "programmes" && (
              <div className="space-y-4" data-testid="academy-programmes">
                {(data.programs || []).every((d) => d.programs.length === 0) && (
                  <div className="border border-dashed border-white/15 p-6 text-center">
                    <Timer size={26} className="text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Programmes d'entraînement en préparation.</p>
                    <p className="kz-mono text-[10px] text-white/35 mt-1">20 / 30 / 45 min · par ceinture — à venir</p>
                  </div>
                )}
                {(data.programs || []).map((grp) => grp.programs.length > 0 && (
                  <div key={grp.duration_min}>
                    <div className="kz-mono text-xs tracking-wider text-[#C9A961] mb-2">{DURATION_LABEL[grp.duration_min]}</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {grp.programs.map((p) => <ProgramCard key={p.id} p={p} onPlay={onPlay} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "competition" && (
              <div className="space-y-3" data-testid="academy-competition">
                {(data.competition || []).length === 0 ? (
                  <div className="border border-dashed border-white/15 p-6 text-center">
                    <Trophy size={26} className="text-white/40 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">Programmes de compétition à venir.</p>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {(data.competition || []).map((p) => <ProgramCard key={p.id} p={p} onPlay={onPlay} competition />)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      {player && <VideoPlayer videoId={player.id} title={player.title} onClose={() => setPlayer(null)} />}
    </div>,
    document.body
  );
}

function ProgramCard({ p, onPlay, competition }) {
  return (
    <div className="border border-white/10 bg-white/[0.02] p-4" data-testid={`academy-program-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-white text-sm font-medium flex items-center gap-2">
          {competition && <Trophy size={14} weight="fill" className="text-[#C9A961]" />}{p.title}
        </span>
        <span className="kz-mono text-[10px] text-white/40">{p.level || ""}</span>
      </div>
      {p.objective && <p className="text-white/55 text-xs mt-1">{p.objective}</p>}
      {p.embed_url && (
        <button onClick={() => onPlay(p.embed_url, p.title)} data-testid={`academy-program-play-${p.id}`}
          className="mt-3 inline-flex items-center gap-2 text-[#C9A961] text-xs hover:opacity-80">
          <PlayCircle size={16} weight="fill" /> Regarder la vidéo
        </button>
      )}
    </div>
  );
}

